import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  House, 
  CurrencyCircleDollar, 
  Users, 
  Vault, 
  ChartBar, 
  SignOut,
  UserPlus,
  Gear,
  TrendUp
} from '@phosphor-icons/react';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: House, label: 'Dashboard' },
    { path: '/loans/new', icon: CurrencyCircleDollar, label: 'New Loan' },
    { path: '/customers', icon: Users, label: 'Customers' },
    { path: '/vault', icon: Vault, label: 'Vault' },
    { path: '/reports', icon: ChartBar, label: 'Reports' },
  ];

  const adminItems = [
    { path: '/users', icon: UserPlus, label: 'Users', roles: ['super_admin', 'admin'] },
    { path: '/settings', icon: Gear, label: 'Settings', roles: ['super_admin', 'admin'] },
  ];

  const isActive = (path) => location.pathname === path;

  if (isMobile) {
    return (
      <div className="min-h-screen bg-[#F9F9FB] pb-20">
        {/* Mobile Header */}
        <div className="bg-white border-b border-[#E5E7EB] px-4 py-3 sticky top-0 z-40">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-[#C9A227]" style={{ fontFamily: 'Work Sans, sans-serif' }}>Gold POS</h1>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#6B7280]">{user?.name}</span>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button">
                <SignOut size={20} />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <Outlet />
        </div>

        {/* Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E7EB] px-2 py-2 z-50">
          <div className="flex items-center justify-around">
            {navItems.slice(0, 4).map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center gap-1 px-3 py-2 rounded-md transition-colors ${
                    active ? 'text-[#C9A227]' : 'text-[#6B7280]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon size={24} weight={active ? 'fill' : 'regular'} />
                  <span className="text-xs">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="min-h-screen bg-[#F9F9FB] flex">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-[#E5E7EB] flex flex-col">
        <div className="p-6 border-b border-[#E5E7EB]">
          <h1 className="text-2xl font-bold text-[#C9A227]" style={{ fontFamily: 'Work Sans, sans-serif' }}>Gold & Silver POS</h1>
          <p className="text-sm text-[#6B7280] mt-1">{user?.role?.replace('_', ' ').toUpperCase()}</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                  active 
                    ? 'bg-[#C9A227] text-white' 
                    : 'text-[#6B7280] hover:bg-[#F9F9FB]'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon size={20} weight={active ? 'fill' : 'regular'} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}

          {user && (user.role === 'super_admin' || user.role === 'admin') && (
            <div className="pt-4 mt-4 border-t border-[#E5E7EB]">
              {adminItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                      active 
                        ? 'bg-[#C9A227] text-white' 
                        : 'text-[#6B7280] hover:bg-[#F9F9FB]'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon size={20} weight={active ? 'fill' : 'regular'} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-[#E5E7EB]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#C9A227] rounded-full flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-[#1A1A1A]">{user?.name}</p>
              <p className="text-xs text-[#6B7280]">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="w-full" 
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <SignOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
